<template>
  <transition name="slide">
    <div
      v-if="openSidebarDrawer"
      v-on-click-outside="() => emit('clickOutSide')"
      class="h-full fixed top-0 right-0 bg-white border-l duration-300 border-gray-200 overflow-x-hidden overflow-y-auto no-scrollbar overscroll-none"
      :class="[roundedLeft && !smAndDown && 'rounded-l-xl']"
      :style="{
        width: smAndDown ? '100%' : `${width}px`,
        zIndex: zIndex + 9,
      }"
    >
      <slot />
    </div>
  </transition>
  <v-overlay v-if="!smAndDown" v-model="showOverlay" :z-index="zIndex" @click:outside="closeModal"></v-overlay>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import useModals from "@/composables/useModals";
import { useModalStore } from "@/stores/modalStore";
import { vOnClickOutside } from "@vueuse/components";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  overlay: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "246",
  },
  roundedLeft: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: () => {
      const sliceLength = Math.ceil(Math.random() * 10);
      const now = Date.now().toString();
      return "mdl" + now.substring(now.length - sliceLength, now.length);
    },
  },
});

const modalStore = useModalStore();
const emit = defineEmits(["update:modelValue", "clickOutSide"]);
const { smAndDown } = useDisplay();

const openSidebarDrawer = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
});

const showOverlay = ref(false);
const activeModal = ref(false);
const zIndex = ref(1000);

onBeforeUnmount(() => {
  useModals().remove(props.name);
});

watch(
  () => openSidebarDrawer.value,
  (val) => {
    if (val) {
      if (props.overlay)
        setTimeout(() => {
          showOverlay.value = true;
        }, 10);
      useModals().add(props.name, closeModal);
    } else {
      useModals().remove(props.name);
      showOverlay.value = false;
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => modalStore.stack,
  (val) => {
    if (val.length) {
      const modal = val.find((item) => item.modalName === props.name);
      if (modal) {
        zIndex.value = modal.zIndex;
        activeModal.value = modal.active;
      }
    }
  },
  { deep: true }
);

const closeModal = () => {
  if (activeModal.value) openSidebarDrawer.value = false;
};
</script>

<style lang="scss" scoped>
.slide-leave-active,
.slide-enter-active {
  transition: all 0.3s ease-out;
}

.slide-enter-from {
  transform: translateX(100%);
}

.slide-leave-to {
  transform: translateX(100%);
}
</style>
